import { useState, useEffect } from 'react';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

/**
 * DemoModal — shared "Request a Demo" modal.
 *
 * Props:
 *   open           {boolean}  — whether the modal is visible
 *   onClose        {function} — called when the user closes the modal
 *   onSignIn       {function} — called when the user clicks "Sign in to your dashboard"
 *                               (optional — falls back to href="/login")
 *   initialFirst   {string}  — pre-fill first name
 *   initialLast    {string}  — pre-fill last name
 *   initialEmail   {string}  — pre-fill email
 *   initialCompany {string}  — pre-fill company
 */
export default function DemoModal({
  open,
  onClose,
  onSignIn,
  initialFirst   = '',
  initialLast    = '',
  initialEmail   = '',
  initialCompany = '',
}) {
  const [first,    setFirst]    = useState('');
  const [last,     setLast]     = useState('');
  const [email,    setEmail]    = useState('');
  const [company,  setCompany]  = useState('');
  const [aum,      setAum]      = useState('');
  const [interest, setInterest] = useState('');
  const [message,  setMessage]  = useState('');
  const [error,    setError]    = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [loading,  setLoading]  = useState(false);

  // Each time the modal opens: reset state and pre-fill from props
  useEffect(() => {
    if (open) {
      setFirst(initialFirst);
      setLast(initialLast);
      setEmail(initialEmail);
      setCompany(initialCompany);
      setAum('');
      setInterest('');
      setMessage('');
      setError(false);
      setSuccess(false);
      setLoading(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setError(false);
    setSuccess(false);
    onClose();
  };

  const handleSignIn = (e) => {
    e.preventDefault();
    handleClose();
    if (onSignIn) setTimeout(onSignIn, 120);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!first || !last || !email || !company) {
      setError(true); setSuccess(false); return;
    }
    setError(false);
    setLoading(true);
    try {
      await axios.post(`${apiUrl}/requestDemo`, {
        firstName: first,
        lastName:  last,
        email,
        company,
        aum,
        interest,
        message,
      });
      setSuccess(true);
      setFirst(''); setLast(''); setEmail(''); setCompany('');
      setAum(''); setInterest(''); setMessage('');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`trg-overlay${open ? ' open' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="trg-modal" style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <div className="modal-logo">
            <svg width="24" height="17" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" />
            </svg>
            <div className="modal-logo-text">
              <div className="modal-logo-main">Tail Risk <span>Global</span></div>
              <div className="modal-logo-sub">Schedule a Demo</div>
            </div>
          </div>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 2l14 14M16 2L2 16" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-title">See TRG in Action</div>
          <div className="modal-subtitle">A risk specialist will walk you through a live, personalised demo</div>

          {success && (
            <div className="modal-success" style={{ display: 'block' }}>
              &#10004;&nbsp;&nbsp;Thank you &#8212; we&#8217;ll be in touch within one business day.
            </div>
          )}
          {error && (
            <div className="modal-error" style={{ display: 'block' }}>
              Please fill in all required fields.
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="modal-row">
              <div className="modal-field">
                <label className="modal-label" htmlFor="dm-first">First Name *</label>
                <input className="modal-input" type="text" id="dm-first" placeholder="Jane" required
                  value={first} onChange={(e) => setFirst(e.target.value)} />
              </div>
              <div className="modal-field">
                <label className="modal-label" htmlFor="dm-last">Last Name *</label>
                <input className="modal-input" type="text" id="dm-last" placeholder="Smith" required
                  value={last} onChange={(e) => setLast(e.target.value)} />
              </div>
            </div>

            <div className="modal-field">
              <label className="modal-label" htmlFor="dm-email">Work Email *</label>
              <input className="modal-input" type="email" id="dm-email" placeholder="jane@yourfirm.com" required
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div className="modal-row">
              <div className="modal-field">
                <label className="modal-label" htmlFor="dm-company">Firm / Company *</label>
                <input className="modal-input" type="text" id="dm-company" placeholder="Acme Capital" required
                  value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>
              <div className="modal-field">
                <label className="modal-label" htmlFor="dm-aum">AUM (approx.)</label>
                <select className="modal-input modal-select" id="dm-aum" value={aum} onChange={(e) => setAum(e.target.value)}>
                  <option value="" disabled>Select range</option>
                  <option>Under $100M</option>
                  <option>$100M &#8211; $250M</option>
                  <option>$250M &#8211; $750M</option>
                  <option>$750M &#8211; $2B</option>
                  <option>$2B &#8211; $5B</option>
                  <option>Over $5B</option>
                </select>
              </div>
            </div>

            <div className="modal-field">
              <label className="modal-label" htmlFor="dm-interest">Primary Interest</label>
              <select className="modal-input modal-select" id="dm-interest" value={interest} onChange={(e) => setInterest(e.target.value)}>
                <option value="" disabled>Select an option</option>
                <option>Core Platform &#8212; Market Value Analytics</option>
                <option>Periodic Risk Reports</option>
                <option>Continuous Risk Monitoring</option>
                <option>Illiquid Asset Risk Methodology</option>
                <option>Full Platform Overview</option>
              </select>
            </div>

            <div className="modal-field">
              <label className="modal-label" htmlFor="dm-message">Anything you&#8217;d like us to know?</label>
              <textarea className="modal-input modal-textarea" id="dm-message"
                placeholder="e.g. asset classes, current pain points, timeline&#8230;"
                value={message} onChange={(e) => setMessage(e.target.value)}>
              </textarea>
            </div>

            <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.6, marginBottom: '0.8rem' }}>
              Outputs are for informational purposes only and do not constitute investment advice.{' '}
              <a href="/doc/Disclaimer.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold-text)', opacity: 0.6, textDecoration: 'underline', textUnderlineOffset: '2px' }}>Full Disclaimer.</a>
            </p>
            <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.6, marginBottom: '0.8rem' }}>
              All plans include a <strong style={{ color: 'rgba(255,255,255,0.55)' }}>30-day free trial</strong> — no payment required until you are ready to subscribe.
              {' '}Trial access is subject to our{' '}
              <a href="/doc/TRG_Trial_Agreement.pdf" download style={{ color: 'var(--gold-text)', opacity: 0.6, textDecoration: 'underline', textUnderlineOffset: '2px' }}>Trial Access Agreement</a>.
            </p>

            <button type="submit" className="modal-submit" disabled={loading}>
              {loading ? 'Sending\u2026' : 'Request My Demo'}
              {!loading && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 7h10M8 3l4 4-4 4" />
                </svg>
              )}
            </button>
          </form>

          <div className="modal-divider"></div>
          <div className="modal-footer-note">
            Already a client?{' '}
            <a href={onSignIn ? '#' : '/login'} onClick={onSignIn ? handleSignIn : undefined}>
              Sign in to your dashboard &#8594;
            </a>
            <br />
            Questions? <a href="mailto:info@tailriskglobal.com">info@tailriskglobal.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}
